import { ElementRef, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { VhImage } from 'vhautowebdb';
import { FunctionService } from 'vhobjects-service/src/services';

@Injectable({
  providedIn: 'root'
})
export class CanvasService {
  // Danh sách menu mục chỉnh ảnh
  listChoose: any[] = [
    { funcName: "cropImage", icon: 'crop', title: 'cat_xen', fileName: "crop-image" },
    { funcName: "rotateImage", icon: 'screen_rotation', title: 'xoay_anh', fileName: "rotate-image" },
    // { funcName: "removeBackground", icon: 'wallpaper', title: 'xoa_nen', fileName: "remove-background-image" },
    // { funcName: "drawOnImage", icon: 'brush', title: 've_len_anh', fileName: "draw-on-image" },
    // { funcName: "addLogo", icon: 'insert_photo', title: 'chen_logo', fileName: "logo" },
  ];
  // Danh sách các đuôi file tương ứng với kiểu khi lưu lên web
  extensionToType = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    // gif: 'image/gif',
    // webp: 'image/webp',
    // svg: 'image/svg+xml',
    // bmp: 'image/bmp',
    // tif: 'image/tiff',
    // tiff: 'image/tiff',
    // ico: 'image/vnd.microsoft.icon',
    // heic: 'image/heic',
  }
  // Danh sách các tỉ lệ cắt ảnh
  ratiosCrop = ['1:1', '1:2', '2:1', '2:3', '3:4', '4:3', '9:16', '16:9'];

  // Danh sách công cụ vẽ
  drawTools = [
    { name: 'pen', label: 'but_ve', matIcon: 'create' },
    { name: 'eraser', label: 'tay_net_ve', matIcon: 'backspace' },
    { name: 'line', label: 'duong_thang', matIcon: 'linear_scale' },
    { name: 'arrow', label: 'mui_ten', matIcon: 'arrow_forward' },
    { name: 'rectangle', label: 'hinh_vuong', matIcon: 'crop_square' },
    { name: 'circle', label: 'hinh_tron', matIcon: 'blur_circular' },
    { name: 'triangle', label: 'hinh_tam_giac', matIcon: 'signal_cellular_4_bar' },
  ];

  canvas!: HTMLCanvasElement;
  ctx!: CanvasRenderingContext2D;

  // Biến tạm để vẽ preview
  previewCanvas!: HTMLCanvasElement;
  previewCtx!: CanvasRenderingContext2D;

  // Thuộc tính cho u2net (remove background opensource)
  private session: any;
  private ort: any;

  constructor(
    private functionService: FunctionService,
    private vhImage: VhImage,
    private translateService: TranslateService,
  ) { }

  /**
   * Hàm thực hiện vẽ hình lên canvas
   * @param canvasRef 
   * @param _this 
   */
  draw(canvasRef: ElementRef<HTMLCanvasElement>, _this): void {
    const canvas = canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    // --- DPR handling (nếu bạn đang dùng canvas với CSS size) ---
    const dpr = window.devicePixelRatio || 1;
    // Nếu canvas CSS size khác pixel size, bạn phải set canvas.width/height = css*w*dpr, etc.
    // Ở đây giả sử canvas.width/height đã đặt bằng pixel-size thực hoặc bạn muốn giữ nguyên.

    // Chuyển đổi deg -> radians
    let angle = _this.rotationAngle || 0;
    // Nếu angle lớn hơn 2π thì rất có khả năng đang truyền degrees -> chuyển sang radians
    if (Math.abs(angle) > 2 * Math.PI) {
      angle = (angle % 360) * Math.PI / 180;
    } else if (Math.abs(angle) > Math.PI) {
      // heuristic: nếu value lớn (ví dụ 90) coi là degrees
      angle = (angle % 360) * Math.PI / 180;
    }
  
    const s = _this.scale || 1;
    const imgW = _this.image.width;
    const imgH = _this.image.height;

    if (_this.activeTool === 'rotate') {
      // Nếu muốn canvas auto-swap khi xoay 90/270 để ảnh không bị cắt (optional)
      const swapCanvasForPerp = false; // true nếu muốn swap width/height khi angle ~ +/-90deg
      const normalizedDeg = (angle * 180 / Math.PI) % 360;
      const isPerp = Math.abs(Math.round(normalizedDeg) % 180) === 90;

      if (swapCanvasForPerp && isPerp) {
        // swap canvas pixel dims (chú ý: cẩn thận nếu canvas có layout phụ thuộc CSS)
        const prevCssW = canvas.clientWidth;
        const prevCssH = canvas.clientHeight;
        // set pixel sizes swapped
        canvas.width = Math.round(prevCssH * dpr);
        canvas.height = Math.round(prevCssW * dpr);
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr, dpr);
      } else {
        // Nếu bạn đang sử dụng DPR, đảm bảo transform dpr nếu cần:
        // ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // gọi nếu bạn set canvas.width/height bằng pixels * dpr
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
    }

    // Vẽ ảnh với chất lượng cao
    this.ctx.save();
    this.ctx.translate(_this.offsetX, _this.offsetY);
    this.ctx.scale(s, s);
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
    this.ctx.drawImage(_this.image, 0, 0, imgW, imgH);
    this.ctx.restore();

    // Vẽ lưới nếu cần
    if (_this.showGrid) {
      this.drawGrid(_this.canvasRef);
    }

    // Vẽ khung crop nếu đang trong chế độ crop
    if (_this.isCropping && _this.activeTool === 'crop') {
      const x = Math.min(_this.cropStartX, _this.cropEndX) * s + _this.offsetX;
      const y = Math.min(_this.cropStartY, _this.cropEndY) * s + _this.offsetY;
      const width = Math.abs(_this.cropEndX - _this.cropStartX) * s;
      const height = Math.abs(_this.cropEndY - _this.cropStartY) * s;

      this.ctx.save();

      // Vẽ overlay
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.ctx.beginPath();
      this.ctx.rect(0, 0, canvas.width, canvas.height);
      this.ctx.rect(x, y, width, height);
      this.ctx.fill('evenodd');
      
      // Vẽ khung
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 2 / s;
      this.ctx.setLineDash([5, 5]);
      this.ctx.strokeRect(x, y, width, height);
      
      // Vẽ các handle
      this.drawCropHandles(x, y, width, height);
      
      this.ctx.restore();
    }

    if (_this.activeTool === 'rotate') {
      this.ctx.save();
      if (!angle) {
        // như cũ: translate offset, scale rồi drawImage tại (0,0)
        this.ctx.translate(_this.offsetX, _this.offsetY);
        this.ctx.scale(s, s);
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        this.ctx.drawImage(_this.image, 0, 0, imgW, imgH);
      } else {
        // Clear (Chống bị double ảnh);
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Khi xoay: translate tới pivot (center of image in canvas), rotate, scale, vẽ ảnh centered
        const pivotX = _this.offsetX + (imgW * s) / 2;
        const pivotY = _this.offsetY + (imgH * s) / 2;
    
        // Vì chúng ta sẽ scale sau, dịch pivot về theo đơn vị canvas trước khi scale:
        this.ctx.translate(pivotX, pivotY);
        this.ctx.rotate(angle);
        // Sau khi rotate, scale theo hệ toạ độ ảnh
        this.ctx.scale(1, 1);
        // this.ctx.scale(s, s);
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        // Vẽ ảnh sao cho tâm ảnh trùng origin
        this.ctx.drawImage(_this.image, -imgW / 2, -imgH / 2, imgW, imgH);
      }
      this.ctx.restore();
    }

    // Cập nhật preview khi đang vẽ hình
    if (this.listChoose[_this.tab].funcName === 'drawOnImage') {
      // Đặt kích thước canvas bằng kích thước ảnh
      this.canvas.width = imgW;
      this.canvas.height = imgH;
      this.previewCanvas.width = imgW;
      this.previewCanvas.height = imgH;
      
      // Vẽ ảnh lên canvas
      this.ctx.drawImage(_this.image, 0, 0, imgW, imgH);
      
      // Lưu trạng thái ban đầu vào lịch sử
      _this.saveState();
    }
  }

  /**
   * Hàm vẽ lưới trên canvas sẽ đè lên hình
   * @param canvasRef 
   */
  drawGrid(canvasRef: ElementRef<HTMLCanvasElement>): void {
    const canvas = canvasRef.nativeElement;
    const step = 20;
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    this.ctx.lineWidth = 1;
    
    this.ctx.beginPath();
    // Vẽ đường dọc
    for (let x = 0; x <= canvas.width; x += step) {
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, canvas.height);
    }
    // Vẽ đường ngang
    for (let y = 0; y <= canvas.height; y += step) {
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(canvas.width, y);
    }
    this.ctx.stroke();
  }

  /**
   * Vẽ các handle điều chỉnh
   * @param x 
   * @param y 
   * @param width 
   * @param height 
   */
  private drawCropHandles(x: number, y: number, width: number, height: number): void {
    const handleSize = 8;
    const handles = [
      { x: x, y: y }, // NW
      { x: x + width/2, y: y }, // N
      { x: x + width, y: y }, // NE
      { x: x, y: y + height/2 }, // W
      { x: x + width, y: y + height/2 }, // E
      { x: x, y: y + height }, // SW
      { x: x + width/2, y: y + height }, // S
      { x: x + width, y: y + height } // SE
    ];
    
    this.ctx.fillStyle = '#ffffff';
    this.ctx.strokeStyle = '#000000';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([]);
    
    for (const handle of handles) {
      this.ctx.beginPath();
      this.ctx.rect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
      this.ctx.fill();
      this.ctx.stroke();
    }
  }

  /**
   * Hàm reset view
   * @param canvasRef 
   * @param _this 
   * @example this.canvasService.resetView(this.canvasRef, this);
   */
  resetView(canvasRef: ElementRef<HTMLCanvasElement>, _this): void {
    // Tính tỷ lệ zoom để ảnh bằng 3/4 container (giữ nguyên tỷ lệ)
    const canvas = canvasRef.nativeElement;
    const scaleX = (canvas.width * 0.75) / _this.image.width;
    const scaleY = (canvas.height * 0.75) / _this.image.height;
    
    _this.scale = this.roundNumber(Math.min(scaleX, scaleY));
    _this.offsetX = (canvas.width - _this.image.width * _this.scale) / 2;
    _this.offsetY = (canvas.height - _this.image.height * _this.scale) / 2;
    _this.currentZoom = Math.round(_this.scale * 100);
    
    this.draw(canvasRef, _this);
  }

  /**
   * Hàm kiểm tra điểm có trong handle không
   * @param x 
   * @param y 
   * @param handle 
   * @param _this 
   * @returns true: có; false: không
   * @example 
   * // Kiểm tra xem có click vào handle nào không
   * if (this.canvasService.isPointInHandle(mouseX, mouseY, handle, this)) {
   *  this.isResizingCrop = true;
   *  this.resizeHandle = handle.type;
   *  return;
   * }
   */
  isPointInHandle(x: number, y: number, handle: any, _this): boolean {
    const handleSize = 8 / _this.scale;
    return x >= handle.x - handleSize && x <= handle.x + handleSize && 
           y >= handle.y - handleSize && y <= handle.y + handleSize;
  }
  
  /**
   * Hàm kiểm tra điểm có trong vùng crop không
   * @param x 
   * @param y 
   * @param _this 
   * @returns true: có; false: không
   * @example
   * // Nếu không click vào handle, kiểm tra có click trong vùng crop không
   * if (this.canvasService.isPointInCropArea(mouseX, mouseY, this)) {
   *  this.isDraggingCrop = true;
   *  this.dragStartX = mouseX;
   *  this.dragStartY = mouseY;
   *  this.cropDragOffsetX = mouseX - Math.min(this.cropStartX, this.cropEndX);
   *  this.cropDragOffsetY = mouseY - Math.min(this.cropStartY, this.cropEndY);
   *  return;
   * }
   */
  isPointInCropArea(x: number, y: number, _this): boolean {
    const minX = Math.min(_this.cropStartX, _this.cropEndX);
    const minY = Math.min(_this.cropStartY, _this.cropEndY);
    const maxX = Math.max(_this.cropStartX, _this.cropEndX);
    const maxY = Math.max(_this.cropStartY, _this.cropEndY);
    
    return x >= minX && x <= maxX && y >= minY && y <= maxY;
  }

  /**
   * Hàm làm tròn số trong thu phóng
   * @param number số truyền vào
   * @param decimals làm tròn mấy số sau phẩy
   * @returns number số sau khi làm tròn
   * @example this.scale = this.canvasService.roundNumber(newScale);
   * // Hoặc
   * this.scale = this.canvasService.roundNumber(newScale, 1);
   */
  roundNumber(number: number, decimals: number = 2): number {
    return Number(number.toFixed(decimals));
  }

  /**
   * Hàm tạo mới canvas để vẽ lại hoặc lưu
   * @param type loại menu công cụ
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example const { tempCanvas, newImage } = await this.canvasService.createNewCanvas(type, this);
   * // Hoặc
   * const { newImage } = await this.canvasService.createNewCanvas('crop', this);
   */
  async createNewCanvas(type: 'crop' | 'rotate' | 'removeBackground' | 'drawOnImage' | 'default', _this): Promise<{ tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement }> {
    const newImage = new Image();
    newImage.crossOrigin = '';
  
    return new Promise((resolve, reject) => {
      newImage.onload = () => {
        let result: { tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement };
  
        switch (type) {
          case 'crop':
            result = this.handleCrop(newImage, _this);
            break;
  
          case 'rotate':
            result = this.handleRotate(newImage, _this);
            break;

          case 'removeBackground':
            result = this.handleRemoveBackground(newImage, _this);
            break;

          case 'drawOnImage':
            result = this.handleDrawOnImage(newImage, _this);
            break;
  
          default:
            result = this.handleDefault(newImage, _this);
            break;
        }
        if (!result) {
          // Nếu result = null, fallback về default
          console.warn('fallback to default, result = ', result);
          result = this.handleDefault(newImage, _this);
        }
        _this.image = newImage;
        this.draw(_this.canvasRef, _this);
  
        resolve(result);
      };
  
      newImage.onerror = () => {
        this.functionService.createMessage('error', 'loi_khi_tai_anh');
        reject('Failed to load image');
      };
  
      newImage.src = _this.image.src;
    });
  }

  /**
   * Xử lý cho loại crop được dùng trong hàm createNewCanvas
   * @param newImage ảnh mới
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example result = this.handleCrop(newImage, _this);
   */
  private handleCrop(newImage: HTMLImageElement, _this): { tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement } | null {
    if (!_this.isCropping) return null;
    
    const x = Math.min(_this.cropStartX, _this.cropEndX);
    const y = Math.min(_this.cropStartY, _this.cropEndY);
    const width = Math.abs(_this.cropEndX - _this.cropStartX);
    const height = Math.abs(_this.cropEndY - _this.cropStartY);

  
    if (width <= 10 || height <= 10) return null;
  
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
  
    const tempCtx = tempCanvas.getContext('2d')!;
    // Đảm bảo chất lượng ảnh
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';
    tempCtx.drawImage(_this.image, x, y, width, height, 0, 0, width, height);
  
    return { tempCanvas, newImage };
  }
  
  /**
   * Xử lý cho loại rotate được dùng trong hàm createNewCanvas
   * @param newImage ảnh mới
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example result = this.handleRotate(newImage, _this);
   */
  private handleRotate(newImage: HTMLImageElement, _this): { tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement } {
    const angle = _this.rotationAngle % 360;
    const isRightAngle = angle % 180 === 90;

    // Chuẩn hoá góc sang degrees trong [0,360)
    let angleDeg = (_this.rotationAngle || 0) % 360;
    if (angleDeg < 0) angleDeg += 360;
    const angleRad = angleDeg * Math.PI / 180;
  
    const sourceWidth = newImage.width;
    const sourceHeight = newImage.height;

    // Nếu góc 90 hoặc 270 thì width/height hoán đổi
    const isPerp = Math.abs(angleDeg % 180 - 90) < 0.0001; // an epsilon check
    const destCanvasW = isPerp ? sourceHeight : sourceWidth;
    const destCanvasH = isPerp ? sourceWidth : sourceHeight;

    // DevicePixelRatio (tuỳ bạn có muốn ảnh lưu cao DPI hay không)
    const dpr = window.devicePixelRatio || 1;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(destCanvasW * dpr);
    tempCanvas.height = Math.round(destCanvasH * dpr);
    // Nếu bạn không muốn scale theo DPR, đặt dpr = 1
    // Code cux
    // const tempCanvas = document.createElement('canvas');
    // tempCanvas.width = isRightAngle ? sourceHeight : sourceWidth;
    // tempCanvas.height = isRightAngle ? sourceWidth : sourceHeight;
  
    const tempCtx = tempCanvas.getContext('2d')!;
    // Reset transform
    tempCtx.setTransform(1, 0, 0, 1, 0, 0);
    // Scale theo DPR để đảm bảo output pixel density
    if (dpr !== 1) tempCtx.scale(dpr, dpr);

    // Chất lượng
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    // Dịch context tới center canvas (theo đơn vị CSS px, not DPR-scaled)
    tempCtx.translate(destCanvasW / 2, destCanvasH / 2);
    tempCtx.rotate(angleRad);

    // Vẽ image với kích thước rõ ràng để giữ tỉ lệ
    tempCtx.drawImage(newImage, -sourceWidth / 2, -sourceHeight / 2, sourceWidth, sourceHeight);
    // const tempCtx = tempCanvas.getContext('2d')!;
    // // Đảm bảo chất lượng ảnh
    // tempCtx.imageSmoothingEnabled = true;
    // tempCtx.imageSmoothingQuality = 'high';
    // tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    // tempCtx.rotate((angle * Math.PI) / 180);
    // tempCtx.drawImage(newImage, -sourceWidth / 2, -sourceHeight / 2);
  
    return { tempCanvas, newImage };
  }

  /**
   * Xử lý cho loại removeBackground được dùng trong hàm createNewCanvas
   * @param newImage ảnh mới
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example result = this.handleRemoveBackground(newImage, _this);
   */
  private handleRemoveBackground(newImage: HTMLImageElement, _this): { tempCanvas: HTMLCanvasElement; newImage: HTMLImageElement; } {
    // 1. Chuẩn bị canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = _this.image.width;
    tempCanvas.height = _this.image.height;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
    if (!tempCtx) throw new Error('Không thể lấy canvas context');

    // Đảm bảo chất lượng hình ảnh
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';
    
    // 2. Lấy dữ liệu ảnh
    tempCtx.drawImage(newImage, 0, 0);

    // Tự code
    // Lấy dữ liệu pixel
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

    // Tính toán tỷ lệ downscale tối ưu
    const scaleFactor = this.calculateOptimalScale(_this.image.width, _this.image.height);
    const scaledData = this.downscaleImage(
      tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height),
      scaleFactor
    );

    // Phát hiện biên (Canny Edge)
    const edgeMask = this.cannyEdgeDetection(imageData);

    // Phân cụm màu (K-means)
    const colorMask = this.kMeansClustering(imageData, _this);

    // Kết hợp mask
    const combinedMask = this.combineMasks(edgeMask, colorMask, tempCanvas.width, tempCanvas.height);

    // Làm mịn biên (Gaussian Blur + Threshold)
    const finalMask = this.refineMask(combinedMask, tempCanvas.width, tempCanvas.height);

    // Phóng to mask về kích thước gốc và áp dụng
    const fullSizeMask = this.upscaleMask(
      finalMask,
      _this.image.width,
      _this.image.height
    );

    // Áp dụng mask lên ảnh gốc
    const originalImageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    this.applyTransparency(originalImageData, fullSizeMask);
    tempCtx.putImageData(originalImageData, 0, 0);

    // End tự code

    // u2net
    // const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    
    // // 3. Xử lý xóa nền
    // const mask = await this.removeBackground(imageData);
    
    // // 4. Áp dụng kết quả
    // const result = new ImageData(imageData.width, imageData.height);
  
    // for (let i = 0; i < imageData.data.length; i += 4) {
    //   result.data[i] = imageData.data[i];     // R
    //   result.data[i + 1] = imageData.data[i + 1]; // G
    //   result.data[i + 2] = imageData.data[i + 2]; // B
    //   result.data[i + 3] = Math.floor(imageData.data[i + 3] * (mask.data[i + 3] / 255)); // Alpha
    // }
    
    // tempCtx.putImageData(result, 0, 0);
    // End u2net

    return { tempCanvas, newImage };
  }

  /**
   * Xử lý cho loại drawOnImage được dùng trong hàm createNewCanvas
   * @param newImage ảnh mới
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example result = this.handleDrawOnImage(newImage, _this);
   */
  private handleDrawOnImage(newImage: HTMLImageElement, _this): { tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement } {
    const scaledWidth = _this.image.width * _this.scale;
    const scaledHeight = _this.image.height * _this.scale;

    const offsetX = (this.canvas.width - scaledWidth) / 2;
    const offsetY = (this.canvas.height - scaledHeight) / 2;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = _this.image.width;
    tempCanvas.height = _this.image.height;
    const tempCtx = tempCanvas.getContext('2d')!;

    tempCtx.drawImage(
      this.canvas,
      offsetX, offsetY,       // Vị trí bắt đầu cắt trên canvas gốc
      scaledWidth, scaledHeight, // Kích thước vùng cần cắt
      0, 0,                   // Vị trí đặt trên canvas đích
      _this.image.width, _this.image.height // Kích thước trên canvas đích
    );

    return { tempCanvas, newImage };
  }

  /**
   * Xử lý cho loại default được dùng trong hàm createNewCanvas (Khi các hàm handleCrop, handleRotate, handle.... bị lỗi sẽ trả về hàm này)
   * @param newImage ảnh mới
   * @param _this 
   * @returns {tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement}
   * @example result = this.handleDefault(newImage, _this);
   */
  private handleDefault(newImage: HTMLImageElement, _this): { tempCanvas: HTMLCanvasElement, newImage: HTMLImageElement } {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = _this.image.width;
    tempCanvas.height = _this.image.height;
    
    const tempCtx = tempCanvas.getContext('2d')!;
    // Đảm bảo chất lượng ảnh
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';
    tempCtx.drawImage(newImage, 0, 0);
  
    return { tempCanvas, newImage };
  }

  // ============Remove background with tự code============

  /**
   * Thuật toán phát hiện biên
   * @param imageData dữ liệu ảnh
   * @returns 
   */
  private cannyEdgeDetection(imageData: ImageData): Uint8Array {
    const { width, height, data } = imageData;
    const edgeMap = new Uint8Array(width * height);

    // 1. Làm mờ bằng Gaussian Blur (5x5 kernel)
    const blurred = this.gaussianBlur(data, width, height);

    // 2. Tính gradient bằng Sobel Operator
    const { gradient, direction } = this.sobelOperator(blurred, width, height);

    // 3. Non-Maximum Suppression (loại bỏ pixel không phải biên mạnh)
    this.nonMaximumSuppression(gradient, direction, edgeMap, width, height);

    // 4. Hysteresis Thresholding (ngưỡng kép để giữ lại biên rõ nét)
    this.hysteresisThresholding(edgeMap, width, height, 50, 150);

    return edgeMap;
  }

  /**
   * Thuật toán phân cụm màu
   * @param imageData dữ liệu ảnh
   * @returns 
   */
  private kMeansClustering(imageData: ImageData, _this): Uint8Array {
    const { width, height, data } = imageData;
    const totalPixels = width * height;
    const clusterMap = new Uint8Array(width * height);
    const k = 3; // Số cụm (foreground, background, vùng chuyển tiếp)

    // 1. Khởi tạo centroids ngẫu nhiên
    // let centroids = this.initCentroids(data, k, _this);
    let centroids = this.initKMeansPlusPlus(data, k, width, height);

    // 2. Lặp tối ưu cụm
    const maxIter = 10;
    for (let iter = 0; iter < maxIter; iter++) {
      const counts = new Array(k).fill(0);
      const sums = new Array(k).fill(null).map(() => [0, 0, 0]);

      for (let i = 0; i < totalPixels; i++) {
        const offset = i * 4;
        const r = data[offset];
        const g = data[offset + 1];
        const b = data[offset + 2];
        const a = data[offset + 3];
  
        // Bỏ pixel trong suốt (nếu có)
        if (a === 0) {
          clusterMap[i] = 0;
          continue;
        }
  
        // 2. Gán pixel vào cluster gần nhất
        let minDist = Infinity;
        let bestCluster = 0;
  
        for (let c = 0; c < k; c++) {
          const [cr, cg, cb] = centroids[c];
          const dist = this.euclidean3(r, g, b, cr, cg, cb);
  
          if (dist < minDist) {
            minDist = dist;
            bestCluster = c;
          }
        }
  
        clusterMap[i] = bestCluster;
        counts[bestCluster]++;
        sums[bestCluster][0] += r;
        sums[bestCluster][1] += g;
        sums[bestCluster][2] += b;
      }
  
      // 3. Cập nhật lại centroids
      for (let c = 0; c < k; c++) {
        if (counts[c] === 0) continue;
        centroids[c] = [
          sums[c][0] / counts[c],
          sums[c][1] / counts[c],
          sums[c][2] / counts[c]
        ];
      }
      // // Gán pixel vào cụm gần nhất
      // this.assignPixelsToClusters(data, centroids, clusterMap, width, height);

      // // Cập nhật centroids
      // centroids = this.updateCentroids(data, clusterMap, k, width, height);
    }

    return clusterMap;
  }

  private euclidean3(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
    return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
  }

  private initKMeansPlusPlus(data: Uint8ClampedArray, k: number, width: number, height: number): number[][] {
    const totalPixels = width * height;
    const centroids: number[][] = [];
  
    // Chọn điểm đầu tiên ngẫu nhiên
    const firstIdx = Math.floor(Math.random() * totalPixels);
    const offset = firstIdx * 4;
    centroids.push([
      data[offset],
      data[offset + 1],
      data[offset + 2]
    ]);
  
    while (centroids.length < k) {
      const distances: number[] = [];
      let totalDist = 0;
  
      for (let i = 0; i < totalPixels; i++) {
        const offset = i * 4;
        const r = data[offset];
        const g = data[offset + 1];
        const b = data[offset + 2];
  
        let minDist = Infinity;
        for (const [cr, cg, cb] of centroids) {
          const dist = this.euclidean3(r, g, b, cr, cg, cb);
          if (dist < minDist) minDist = dist;
        }
  
        distances[i] = minDist;
        totalDist += minDist;
      }
  
      // Chọn điểm tiếp theo dựa trên phân phối xác suất
      const rand = Math.random() * totalDist;
      let accum = 0;
      for (let i = 0; i < distances.length; i++) {
        accum += distances[i];
        if (accum >= rand) {
          const offset = i * 4;
          centroids.push([
            data[offset],
            data[offset + 1],
            data[offset + 2]
          ]);
          break;
        }
      }
    }
  
    return centroids;
  }

  /**
   * Kết hợp Edge Mask và Color Mask
   * @param edgeMask 
   * @param colorMask 
   * @param width 
   * @param height 
   * @returns 
   */
  private combineMasks(
    edgeMask: Uint8Array,
    colorMask: Uint8Array,
    width: number,
    height: number
  ): Uint8Array {
    const combined = new Uint8Array(width * height);

    for (let i = 0; i < combined.length; i++) {
      // Pixel là foreground nếu:
      // - Có biên mạnh (edgeMask[i] > 0) HOẶC
      // - Thuộc cụm foreground (colorMask[i] === 0)
      combined[i] = (edgeMask[i] > 0 || colorMask[i] === 0) ? 255 : 0;
    }

    return combined;
  }

  /**
   * Làm mịn mask bằng Gaussian Blur + Threshold
   * @param mask 
   * @param width 
   * @param height 
   * @returns Uint8Array
   */
  private refineMask(mask: Uint8Array, width: number, height: number): Uint8Array {
    const blurred = this.gaussianBlur(mask, width, height);
    const refined = new Uint8Array(width * height);

    // Ngưỡng để giữ lại foreground
    const threshold = 128;
    for (let i = 0; i < refined.length; i++) {
      refined[i] = blurred[i] > threshold ? 255 : 0;
    }

    return refined;
  }

  /**
   * Áp dụng mask để làm trong suốt nền
   * @param imageData 
   * @param mask 
   */
  private applyTransparency(imageData: ImageData, mask: Uint8Array): void {
    const { data, width } = imageData;

    for (let i = 0; i < mask.length; i++) {
      const alpha = mask[i]; // 0: background, 255: foreground
      const pixelIdx = i * 4;

      // Nếu là background, đặt alpha = 0 (trong suốt)
      if (alpha === 0) {
        data[pixelIdx + 3] = 0;
      }
    }
  }
  
  /**
   * Làm mờ ảnh bằng Gaussian Blur
   * @param data 
   * @param width 
   * @param height 
   * @param radius 
   * @returns 
   */
  private gaussianBlur(
    data: Uint8ClampedArray | Uint8Array,
    width: number,
    height: number,
    radius = 2
  ): Uint8Array {
    const kernel = [1, 4, 6, 4, 1]; // Gaussian kernel đơn giản
    const result = new Uint8Array(data.length);
  
    // Áp dụng blur theo chiều ngang
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        let weightSum = 0;
  
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
  
          const kernelVal = kernel[dx + radius];
          const pixelIdx = y * width + nx;
          sum += data[pixelIdx] * kernelVal;
          weightSum += kernelVal;
        }
  
        result[y * width + x] = sum / weightSum;
      }
    }
  
    // Áp dụng blur theo chiều dọc
    const finalResult = new Uint8Array(data.length);
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        let sum = 0;
        let weightSum = 0;
  
        for (let dy = -radius; dy <= radius; dy++) {
          const ny = y + dy;
          if (ny < 0 || ny >= height) continue;
  
          const kernelVal = kernel[dy + radius];
          const pixelIdx = ny * width + x;
          sum += result[pixelIdx] * kernelVal;
          weightSum += kernelVal;
        }
  
        finalResult[y * width + x] = sum / weightSum;
      }
    }
  
    return finalResult;
  }

  /**
   * Tính Gradient
   * @param data 
   * @param width 
   * @param height 
   * @returns 
   */
  private sobelOperator(
    data: Uint8Array,
    width: number,
    height: number
  ): { gradient: Float32Array; direction: Float32Array } {
    const gradient = new Float32Array(width * height);
    const direction = new Float32Array(width * height);
  
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
  
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0;
        let gy = 0;
  
        // Áp dụng kernel Sobel
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const pixelIdx = (y + dy) * width + (x + dx);
            const kernelIdx = (dy + 1) * 3 + (dx + 1);
  
            gx += data[pixelIdx] * sobelX[kernelIdx];
            gy += data[pixelIdx] * sobelY[kernelIdx];
          }
        }
  
        const idx = y * width + x;
        gradient[idx] = Math.sqrt(gx * gx + gy * gy);
        direction[idx] = Math.atan2(gy, gx);
      }
    }
  
    return { gradient, direction };
  }

  /**
   * Khởi tạo tâm cụm (centroids) cho thuật toán K-means
   * Áp dụng phương pháp K-means++ để cải thiện chất lượng khởi tạo
   * 
   * @param data - Mảng pixel ảnh (RGBA)
   * @param k - Số cụm cần phân (thường là 3: foreground, background, vùng chuyển tiếp)
   * @returns Mảng centroids (mỗi centroid là [R, G, B])
   */
  private initCentroids(data: Uint8ClampedArray, k: number, _this): number[][] {
    const centroids: number[][] = [];
    const width = _this.canvasRef.nativeElement.width;
    const height = _this.canvasRef.nativeElement.height;
    const numPixels = width * height;

    // Bước 1: Chọn centroid đầu tiên ngẫu nhiên
    const firstIdx = Math.floor(Math.random() * numPixels) * 4;
    centroids.push([
      data[firstIdx],     // R
      data[firstIdx + 1], // G
      data[firstIdx + 2]  // B
    ]);

    // Bước 2: Chọn các centroid tiếp theo theo K-means++
    for (let c = 1; c < k; c++) {
      const distances = new Float32Array(numPixels);
      let totalDistance = 0;

      // Tính khoảng cách từ mỗi pixel đến centroid gần nhất
      for (let i = 0; i < numPixels; i++) {
        const pixelIdx = i * 4;
        const r = data[pixelIdx];
        const g = data[pixelIdx + 1];
        const b = data[pixelIdx + 2];

        // Tìm centroid gần nhất
        let minDist = Infinity;
        for (const centroid of centroids) {
          const dr = r - centroid[0];
          const dg = g - centroid[1];
          const db = b - centroid[2];
          const dist = dr * dr + dg * dg + db * db; // Euclidean distance squared
          minDist = Math.min(minDist, dist);
        }

        distances[i] = minDist;
        totalDistance += minDist;
      }

      // Chọn pixel có xác suất tỷ lệ với distances^2
      let threshold = Math.random() * totalDistance;
      let sum = 0;
      let selectedIdx = 0;

      for (let i = 0; i < distances.length; i++) {
        sum += distances[i];
        if (sum >= threshold) {
          selectedIdx = i * 4;
          break;
        }
      }

      // Thêm centroid mới
      centroids.push([
        data[selectedIdx],
        data[selectedIdx + 1],
        data[selectedIdx + 2]
      ]);
    }

    return centroids;
  }
  
  /**
   * Thực hiện Non-Maximum Suppression để làm mỏng đường biên
   * @param gradient - Mảng chứa độ lớn gradient
   * @param direction - Mảng chứa hướng gradient (radian)
   * @param edgeMap - Mảng kết quả sau khi áp dụng suppression
   * @param width - Chiều rộng ảnh
   * @param height - Chiều cao ảnh
   */
  private nonMaximumSuppression(
    gradient: Float32Array,
    direction: Float32Array,
    edgeMap: Uint8Array,
    width: number,
    height: number
  ): void {
    // Làm tròn hướng gradient về 4 hướng chính (0°, 45°, 90°, 135°)
    const angles = direction.map(angle => {
      const deg = (angle * 180) / Math.PI;
      const normalized = (deg < 0 ? deg + 180 : deg) % 180;
      
      if ((normalized >= 0 && normalized < 22.5) || (normalized >= 157.5 && normalized <= 180)) {
        return 0;   // Hướng ngang →
      } else if (normalized >= 22.5 && normalized < 67.5) {
        return 45;  // Hướng chéo ↗
      } else if (normalized >= 67.5 && normalized < 112.5) {
        return 90;  // Hướng dọc ↑
      } else {
        return 135; // Hướng chéo ↖
      }
    });

    // Duyệt qua từng pixel (trừ viền)
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const currentGradient = gradient[idx];
        const angle = angles[idx];

        let neighbor1: number, neighbor2: number;

        // Xác định 2 pixel lân cận dựa trên hướng gradient
        switch (angle) {
          case 0:   // So sánh với pixel trái và phải
            neighbor1 = gradient[idx - 1];
            neighbor2 = gradient[idx + 1];
            break;
          case 45:  // So sánh với pixel chéo trên-trái và chéo dưới-phải
            neighbor1 = gradient[idx - width - 1];
            neighbor2 = gradient[idx + width + 1];
            break;
          case 90:  // So sánh với pixel trên và dưới
            neighbor1 = gradient[idx - width];
            neighbor2 = gradient[idx + width];
            break;
          case 135: // So sánh với pixel chéo trên-phải và chéo dưới-trái
            neighbor1 = gradient[idx - width + 1];
            neighbor2 = gradient[idx + width - 1];
            break;
          default:
            neighbor1 = 0;
            neighbor2 = 0;
        }

        // Giữ lại pixel nếu gradient tại đó là cực đại địa phương
        edgeMap[idx] = (currentGradient >= neighbor1 && currentGradient >= neighbor2)
          ? gradient[idx] // Giữ nguyên giá trị gradient
          : 0;            // Suppress (đặt về 0)
      }
    }
  }

  /**
   * Thực hiện hysteresis thresholding để loại bỏ nhiễu và nối các đường biên đứt đoạn
   * @param edgeMap - Mảng chứa thông tin biên sau non-maximum suppression
   * @param width - Chiều rộng ảnh
   * @param height - Chiều cao ảnh
   * @param lowThreshold - Ngưỡng dưới (thường 0.1-0.3 max gradient)
   * @param highThreshold - Ngưỡng trên (thường 0.3-0.7 max gradient)
   */
  private hysteresisThresholding(
    edgeMap: Uint8Array,
    width: number,
    height: number,
    lowThreshold: number,
    highThreshold: number
  ): void {
    if (width * height > 500000) { // Ảnh quá lớn
      this.functionService.createMessage('warning', 'Ảnh quá lớn, cân nhắc giảm kích thước trước khi xử lý');
      return;
    }

    console.log('Hysteresis thresholding...', edgeMap);

    const stack: [number, number][] = [];
    const maxIterations = width * height * 2; // An toàn cho ảnh lớn
    let iterations = 0;
    const maxGradient = Math.max(...edgeMap);
    const normalizedLow = lowThreshold * maxGradient;
    const normalizedHigh = highThreshold * maxGradient;
  
    // Đánh dấu strong/weak edges
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const val = edgeMap[idx];
        
        if (val >= normalizedHigh) {
          edgeMap[idx] = 255; // Strong edge
          stack.push([x, y]);
        } else if (val >= normalizedLow) {
          edgeMap[idx] = 50;  // Weak edge
        } else {
          edgeMap[idx] = 0;    // Suppressed
        }
      }
    }
  
    // Xử lý weak edges liên kết với strong edges
    while (stack.length > 0 && iterations++ < maxIterations) {
      const [x, y] = stack.pop()!;
      
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          
          const nx = x + dx;
          const ny = y + dy;
          if (nx <= 0 || nx >= width - 1 || ny <= 0 || ny >= height - 1) continue;
          
          const nIdx = ny * width + nx;
          // if (edgeMap[nIdx] === 50) {
          //   edgeMap[nIdx] = 255;
          //   stack.push([nx, ny]);
          // }
          if (edgeMap[nIdx] === 50) { // Chỉ xử lý weak edge
            // Kiểm tra 8 lân cận xem có strong edge nào không
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                const neighborIdx = (y + dy) * width + (x + dx);
                if (edgeMap[neighborIdx] === 255) {
                  edgeMap[nIdx] = 255;
                  stack.push([nx, ny]);
                  break;
                }
              }
            }
    
            // Nếu có strong edge lân cận, giữ lại weak edge
          }
        }
      }
    }
  
    // Đảm bảo tất cả weak edges còn lại bị suppress
    for (let i = 0; i < edgeMap.length; i++) {
      // if (edgeMap[i] === 50) edgeMap[i] = 0;
      edgeMap[i] = edgeMap[i] === 255 ? 255 : 0;
    }
  }

  /**
   * Giảm kích thước ảnh bằng phương pháp lấy mẫu trung bình
   * @param imageData - Dữ liệu ảnh gốc (ImageData)
   * @param scaleFactor - Tỷ lệ giảm (0.1 đến 1.0)
   * @returns ImageData - Ảnh đã được giảm kích thước
   */
  private downscaleImage(
    imageData: ImageData, 
    scaleFactor: number
  ): ImageData {
    const srcWidth = imageData.width;
    const srcHeight = imageData.height;
    const srcData = imageData.data;
    
    // Tính kích thước mới
    const dstWidth = Math.max(1, Math.floor(srcWidth * scaleFactor));
    const dstHeight = Math.max(1, Math.floor(srcHeight * scaleFactor));
    
    // Tạo canvas đích
    const canvas = document.createElement('canvas');
    canvas.width = dstWidth;
    canvas.height = dstHeight;
    const ctx = canvas.getContext('2d')!;
    const dstImageData = ctx.createImageData(dstWidth, dstHeight);
    const dstData = dstImageData.data;
    
    // Tính tỷ lệ lấy mẫu
    const xRatio = srcWidth / dstWidth;
    const yRatio = srcHeight / dstHeight;
    
    // Lấy mẫu theo khu vực
    for (let y = 0; y < dstHeight; y++) {
      for (let x = 0; x < dstWidth; x++) {
        // Tính vùng ảnh gốc tương ứng
        const srcX = Math.floor(x * xRatio);
        const srcY = Math.floor(y * yRatio);
        const srcX2 = Math.min(srcWidth - 1, Math.ceil((x + 1) * xRatio));
        const srcY2 = Math.min(srcHeight - 1, Math.ceil((y + 1) * yRatio));
        
        // Tính trung bình khu vực
        let r = 0, g = 0, b = 0, a = 0;
        let count = 0;
        
        for (let sy = srcY; sy < srcY2; sy++) {
          for (let sx = srcX; sx < srcX2; sx++) {
            const srcIdx = (sy * srcWidth + sx) * 4;
            r += srcData[srcIdx];
            g += srcData[srcIdx + 1];
            b += srcData[srcIdx + 2];
            a += srcData[srcIdx + 3];
            count++;
          }
        }
        
        // Gán giá trị trung bình vào ảnh đích
        const dstIdx = (y * dstWidth + x) * 4;
        dstData[dstIdx] = r / count;     // R
        dstData[dstIdx + 1] = g / count; // G
        dstData[dstIdx + 2] = b / count; // B
        dstData[dstIdx + 3] = a / count; // A
      }
    }
    
    return dstImageData;
  }

  /**
   * Tính scale thu/phóng lên cho ảnh
   * @param width 
   * @param height 
   * @returns 
   */
  private calculateOptimalScale(width: number, height: number): number {
    const maxPixels = 300000; // Ngưỡng an toàn về hiệu năng
    const currentPixels = width * height;
    
    if (currentPixels > maxPixels) {
      return Math.sqrt(maxPixels / currentPixels); // Giữ tỷ lệ aspect ratio
    }
    return 1.0; // Không cần downscale
  }
  
  /**
   * Tính scale phóng to lên cho mask
   * @param mask 
   * @param targetWidth 
   * @param targetHeight 
   * @returns 
   */
  private upscaleMask(
    mask: Uint8Array,
    targetWidth: number,
    targetHeight: number
  ): Uint8Array {
    const srcSize = Math.sqrt(mask.length);
    const srcWidth = Math.floor(srcSize);
    const srcHeight = Math.floor(mask.length / srcWidth);
    const result = new Uint8Array(targetWidth * targetHeight);
    const scaleX = targetWidth / srcWidth;
    const scaleY = targetHeight / srcHeight;
  
    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        const srcX = Math.floor(x / scaleX);
        const srcY = Math.floor(y / scaleY);
        const srcIdx = srcY * srcWidth + srcX;
        const dstIdx = y * targetWidth + x;
        result[dstIdx] = mask[srcIdx];
      }
    }
  
    return result;
  }

  // Sử dụng Web Workers để xử lý song song
  private createWorker(): Worker {
    const workerCode = `
      self.onmessage = function(e) {
        const {data, width, height, operation} = e.data;
        let result;
        
        switch(operation) {
          case 'edge':
            result = edgeDetection(data, width, height);
            break;
          case 'color':
            result = colorClustering(data, width, height);
            break;
          // ... other operations
        }
        
        self.postMessage({result}, [result.buffer]);
      };
      
      // Các hàm xử lý ảnh...
    `;
    
    const blob = new Blob([workerCode], {type: 'application/javascript'});
    return new Worker(URL.createObjectURL(blob));
  }

  // Sử dụng WebAssembly nếu cần xử lý nặng
  private async loadWasmModule(): Promise<any> {
    const response = await fetch('assets/wasm/image_processor.wasm');
    const buffer = await response.arrayBuffer();
    const module = await WebAssembly.compile(buffer);
    return new WebAssembly.Instance(module);
  }

  // ============End Remove backgrouùd with tự code============

  // ============Remove backgrouùd with u2net===========
  // async initializeModel(): Promise<void> {
  //   // Tải ONNX Runtime từ CDN
  //   if (!this.ort) {
  //     await this.loadScript('https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/ort.min.js');
  //     this.ort = (window as any).ort;
  //   }
  //   if (!this.session) {
  //     try {
  //       const modelPath = '/assets/models/u2netp.onnx';
  //       this.session = await this.ort.InferenceSession.create(modelPath);
  //     } catch (e) {
  //       console.error('Lỗi tải mô hình:', e);
  //       throw e;
  //     }
  //   }
  // }

  // private loadScript(src: string): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     if (document.querySelector(`script[src="${src}"]`)) {
  //       resolve();
  //       return;
  //     }

  //     const script = document.createElement('script');
  //     script.src = src;
  //     script.onload = () => resolve();
  //     script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
  //     document.head.appendChild(script);
  //   });
  // }

  // async removeBackground(imageData: ImageData): Promise<ImageData> {
  //   await this.initializeModel();

  //   return this.functionService.showLoading(this.translateService.instant('dang_xu_ly_hinh_anh') + '...', "current-loading", null, 0, false)
  //     .then(async () => {
  //       try {
  //         // 1. Tiền xử lý
  //         const { tensor, originalSize } = await this.preprocessImage(imageData);

  //         // 2. Chạy model
  //         const feeds = { 'input.1': tensor };
  //         const outputs = await this.session.run(feeds);
  //         const mask = outputs['1959'] || outputs[Object.keys(outputs)[0]];

  //         // 3. Hậu xử lý
  //         return this.postprocessOutput(mask, originalSize.width, originalSize.height);
  //       } catch (e) {
  //         console.error(e);
  //         this.functionService.hideLoading(200);
  //         throw e;
  //       }
  //     });
  // }

  // private async preprocessImage(imageData: ImageData): Promise<{ tensor: any, originalSize: { width: number, height: number } }> {
  //   const TARGET_SIZE = 320;
  //   const { width, height } = imageData;
    
  //   // Tạo canvas để resize
  //   const canvas = document.createElement('canvas');
  //   canvas.width = TARGET_SIZE;
  //   canvas.height = TARGET_SIZE;
  //   const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;

  //   // ctx.fillStyle = '#000';
  //   ctx.fillStyle = 'rgb(127, 127, 127)';
  //   ctx.fillRect(0, 0, TARGET_SIZE, TARGET_SIZE);

  //   // Tính toán tỷ lệ và padding
  //   const scale = Math.min(TARGET_SIZE / width, TARGET_SIZE / height);
  //   const scaledWidth = Math.floor(width * scale);
  //   const scaledHeight = Math.floor(height * scale);
  //   const offsetX = (TARGET_SIZE - scaledWidth) / 2;
  //   const offsetY = (TARGET_SIZE - scaledHeight) / 2;
    
  //   // Vẽ ảnh vào canvas với padding
  //   const tempCanvas = document.createElement('canvas');
  //   tempCanvas.width = width;
  //   tempCanvas.height = height;
  //   const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  //   tempCtx.putImageData(imageData, 0, 0);
  //   ctx.drawImage(tempCanvas, 0, 0, width, height, offsetX, offsetY, scaledWidth, scaledHeight);
    
  //   // // Tính toán tỷ lệ
  //   // const ratio = Math.min(TARGET_SIZE / width, TARGET_SIZE / height);
  //   // const newWidth = Math.floor(width * ratio);
  //   // const newHeight = Math.floor(height * ratio);
    
  //   // // Vẽ ảnh vào canvas đích
  //   // ctx.drawImage(tempCanvas, 0, 0, width, height, 
  //   //              (TARGET_SIZE - newWidth) / 2, (TARGET_SIZE - newHeight) / 2, 
  //   //              newWidth, newHeight);
    
  //   // Lấy dữ liệu pixel
  //   const processedData = ctx.getImageData(0, 0, TARGET_SIZE, TARGET_SIZE);
    
  //   // Chuẩn bị tensor
  //   const inputTensor = new Float32Array(3 * TARGET_SIZE * TARGET_SIZE);
  //   for (let i = 0; i < processedData.data.length; i += 4) {
  //     const idx = i / 4;
  //     inputTensor[idx] = processedData.data[i] / 255.0;
  //     inputTensor[TARGET_SIZE * TARGET_SIZE + idx] = processedData.data[i + 1] / 255.0;
  //     inputTensor[2 * TARGET_SIZE * TARGET_SIZE + idx] = processedData.data[i + 2] / 255.0;
  //     // TODO: Xử lý lại chỗ này

  //     // inputTensor[idx] = (processedData.data[i] / 255.0 - 0.485) / 0.229;
  //     // inputTensor[TARGET_SIZE * TARGET_SIZE + idx] = (processedData.data[i + 1] / 255.0 - 0.456) / 0.224;
  //     // inputTensor[2 * TARGET_SIZE * TARGET_SIZE + idx] = (processedData.data[i + 2] / 255.0 - 0.406) / 0.225;
  //   }
    
  //   return {
  //     tensor: new this.ort.Tensor('float32', inputTensor, [1, 3, TARGET_SIZE, TARGET_SIZE]),
  //     originalSize: { width, height }
  //   };
  // }

  // private postprocessOutput(mask: any, originalWidth: number, originalHeight: number): ImageData {
  //   const TARGET_SIZE = 320;
  //   const maskData = mask.data;
  //   const result = new ImageData(originalWidth, originalHeight);
    
  //   // Tạo canvas để scale mask về kích thước gốc
  //   const maskCanvas = document.createElement('canvas');
  //   maskCanvas.width = TARGET_SIZE;
  //   maskCanvas.height = TARGET_SIZE;
  //   const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
    
  //   // Tạo mask từ output
  //   const maskImageData = maskCtx.createImageData(TARGET_SIZE, TARGET_SIZE);
  //   for (let i = 0; i < maskData.length; i++) {
  //     const threshold = 0.5;
  //     // Sử dụng sigmoid để có ngưỡng mềm mại
  //     const val = 1 / (1 + Math.exp(-(maskData[i] - threshold) * 10)); // Điều chỉnh 0.7 và 10 để thay đổi độ chính xác
  //     const alpha = Math.floor(val * 255);
  //     maskImageData.data[i * 4 + 3] = alpha;
  //   }
  //   maskCtx.putImageData(maskImageData, 0, 0);
    
  //   // Scale về kích thước gốc
  //   const resultCanvas = document.createElement('canvas');
  //   resultCanvas.width = originalWidth;
  //   resultCanvas.height = originalHeight;
  //   const resultCtx = resultCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  //   resultCtx.imageSmoothingEnabled = true;
  //   resultCtx.imageSmoothingQuality = 'high';
  //   resultCtx.drawImage(maskCanvas, 
  //     0, 0, TARGET_SIZE, TARGET_SIZE,
  //     0, 0, originalWidth, originalHeight
  //   );

  //   // Áp dụng Gaussian blur nhẹ để làm mượt biên
  //   // const blurredMask = this.applyGaussianBlur(resultCtx.getImageData(0, 0, originalWidth, originalHeight), 1);
  //   const blurredMask = resultCtx.getImageData(0, 0, originalWidth, originalHeight);
  //   resultCtx.putImageData(blurredMask, 0, 0);
    
  //   this.functionService.createMessage('success', 'xoa_nen_thanh_cong');
  //   this.functionService.hideLoading(200);
  //   return resultCtx.getImageData(0, 0, originalWidth, originalHeight);
  // }
  // ============End Remove backgrouùd with u2net===========

  // ===========Draw On Image===========
  /**
   * Hàm vẽ hình vuông/chữ nhật
   * @param startX Điểm bắt đầu trục x khi vẽ
   * @param startY Điểm bắt đầu trục y khi vẽ
   * @param endX Điểm kết thúc trục x khi vẽ
   * @param endY Điểm kết thúc trục y khi vẽ
   */
  drawRectangle(startX: number, startY: number, endX: number, endY: number): void {
    this.ctx.strokeRect(startX, startY, endX - startX, endY - startY);
  }

  /**
   * Hàm vẽ hình tròn: sau khi lấy 4 chỉ số bắt đầu trực x, y, kết thúc trực x, y thì radius cho tròn lại
   * @param startX Điểm bắt đầu trục x khi vẽ
   * @param startY Điểm bắt đầu trục y khi vẽ
   * @param endX Điểm kết thúc trục x khi vẽ
   * @param endY Điểm kết thúc trục y khi vẽ
   */
  drawCircle(startX: number, startY: number, endX: number, endY: number): void {
    const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    this.ctx.beginPath();
    this.ctx.arc(startX, startY, radius, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  /**
   * Hàm vẽ đường thẳng hoặc đường thẳng có mũi tên
   * @param startX Điểm bắt đầu trục x khi vẽ
   * @param startY Điểm bắt đầu trục y khi vẽ
   * @param endX Điểm kết thúc trục x khi vẽ
   * @param endY Điểm kết thúc trục y khi vẽ
   * @param isArrow có mũi tên hay không
   * @param _this 
   */
  drawLine(startX: number, startY: number, endX: number, endY: number, isArrow: boolean, _this): void {
    this.ctx.beginPath();
    this.ctx.moveTo(startX, startY);
    this.ctx.lineTo(endX, endY);
    this.ctx.stroke();
    
    if (isArrow) {
      this.drawArrowhead(startX, startY, endX, endY, _this);
    }
  }
  
  /**
   * Hàm vẽ hình tam giác
   * @param startX Điểm bắt đầu trục x khi vẽ
   * @param startY Điểm bắt đầu trục y khi vẽ
   * @param endX Điểm kết thúc trục x khi vẽ
   * @param endY Điểm kết thúc trục y khi vẽ
   */
  drawTriangle(startX: number, startY: number, endX: number, endY: number): void {
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    
    this.ctx.beginPath();
    this.ctx.moveTo(startX, endY);
    this.ctx.lineTo(endX, endY);
    this.ctx.lineTo(midX, startY);
    this.ctx.closePath();
    this.ctx.stroke();
  }

  drawPreview(startX: number, startY: number, endX: number, endY: number, _this): void {
    this.previewCtx.clearRect(0, 0, this.previewCanvas.width, this.previewCanvas.height);
    this.previewCtx.strokeStyle = _this.currentDrawColor;
    this.previewCtx.lineWidth = _this.currentLineWidth;
    this.previewCtx.setLineDash([5, 5]); // Đường nét đứt cho preview
    
    switch (_this.currentTool) {
      case 'rectangle':
        this.previewCtx.strokeRect(startX, startY, endX - startX, endY - startY);
        break;
        
      case 'circle':
        const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        this.previewCtx.beginPath();
        this.previewCtx.arc(startX, startY, radius, 0, Math.PI * 2);
        this.previewCtx.stroke();
        break;
        
      case 'line':
        this.drawLinePreview(startX, startY, endX, endY, false, _this);
        break;
        
      case 'arrow':
        this.drawLinePreview(startX, startY, endX, endY, true, _this);
        break;
        
      case 'triangle':
        this.drawTrianglePreview(startX, startY, endX, endY);
        break;
    }
    
    // Vẽ preview lên canvas chính
    this.ctx.drawImage(this.previewCanvas, 0, 0);
  }
  
  private drawLinePreview(startX: number, startY: number, endX: number, endY: number, isArrow: boolean, _this): void {
    this.previewCtx.beginPath();
    this.previewCtx.moveTo(startX, startY);
    this.previewCtx.lineTo(endX, endY);
    this.previewCtx.stroke();
    
    if (isArrow) {
      this.drawArrowhead(startX, startY, endX, endY, _this);
    }
  }
  
  private drawArrowhead(startX: number, startY: number, endX: number, endY: number, _this): void {
    const headLength = _this.currentLineWidth * 3;
    const angle = Math.atan2(endY - startY, endX - startX);
    
    this.previewCtx.beginPath();
    this.previewCtx.moveTo(endX, endY);
    this.previewCtx.lineTo(
      endX - headLength * Math.cos(angle - Math.PI / 6),
      endY - headLength * Math.sin(angle - Math.PI / 6)
    );
    this.previewCtx.moveTo(endX, endY);
    this.previewCtx.lineTo(
      endX - headLength * Math.cos(angle + Math.PI / 6),
      endY - headLength * Math.sin(angle + Math.PI / 6)
    );
    this.previewCtx.stroke();
  }
  
  private drawTrianglePreview(startX: number, startY: number, endX: number, endY: number): void {
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    
    this.previewCtx.beginPath();
    this.previewCtx.moveTo(startX, endY);
    this.previewCtx.lineTo(endX, endY);
    this.previewCtx.lineTo(midX, startY);
    this.previewCtx.closePath();
    this.previewCtx.stroke();
  }
  
  clearPreview(): void {
    this.previewCtx.clearRect(0, 0, this.previewCanvas.width, this.previewCanvas.height);

    // Đảm bảo các thuộc tính vẽ mặc định
    this.previewCtx.setLineDash([]);
    this.previewCtx.lineCap = 'round';
    this.previewCtx.lineJoin = 'round';
    this.previewCtx.globalCompositeOperation = 'source-over';
  }
  // ============End Draw On Image===========
  
  /**
   * Hàm dùng chung để lưu file ảnh xử lý canvas
   * @param _this 
   * @param objectChoosing object đang được chọn
   * @param tempCanvas canvas tạm thời
   * @example this.canvasService.saveNewImage(this, this.objectChoosing, tempCanvas);
   */
  async saveNewImage(_this, objectChoosing, tempCanvas: HTMLCanvasElement) {
    try {
      const blob = await new Promise<Blob|null>((resolve) => {
        tempCanvas.toBlob(resolve, 'image/png', 1.0); // Chất lượng 100%
      });

      if (!blob) {
        throw new Error('Failed to create blob');
      }

      let compress_type = objectChoosing.setups.upload_image.compress_type;
  
      let resolution = {};
      switch (compress_type) {
        case 'compress-screen':
          resolution = {
            width: _this.resolution[_this.device + '_width'],
            height: 0,
          };
          break;
        case 'compress-frame':
          resolution = {
            width: this.functionService.devices.find((device) => device.value == _this.device)?.width,
            height: this.functionService.devices.find((device) => device.value == _this.device)?.height,
          };
          break;
        case 'custom':
          resolution = {
            width: +_this.compressWidth,
            height: +_this.compressHeight,
          };
          compress_type = 'compress-frame';
          break;
      }

      const newFile = new File([blob], `${_this.fileImg.name}.${_this.fileImg.extension}`, { type: _this.fileImg.type });
      const res = await this.vhImage.getImageFromDesktop_Autoweb(newFile, 'images/design/objects', {
        compress_type: compress_type,
        resolution: resolution,
      });
      
      if (res.vcode != 0) {
        throw new Error(res.msg);
      }

      _this.updateImage(res.data);
      this.functionService.createMessage('success', 'tai_anh_thanh_cong');
    } catch (error) {
      this.functionService.createMessage('error', 'xu_ly_anh_khong_thanh_cong');
      console.error('Save rotated image error:', error);
      throw error;
    }    
  }
}
